/*
 * CONTACT UTILITIES
 * 
 * Contact-related utility functions and configurations for display and search.
 * 
 * Key Features:
 * - Contact field definitions for search and display
 * - Separate configurations for search vs card display
 * - Contact grid column definitions for table view
 * - Contact filtering with customizable search fields
 * 
 * Functions:
 * - getContactDisplayFields(): Returns all searchable contact fields (includes displayName for search)
 * - getContactCardDisplayFields(): Returns fields to display in contact cards (excludes displayName since it's shown as card title)
 * - getContactColumnDefs(): Generates ag-grid column definitions
 * - filterContacts(): Filters contact array by search query
 * 
 * Changes:
 * - Added getContactCardDisplayFields() to separate card display from search fields
 * - displayName included in search but excluded from card details to avoid duplication
 */

import { Contact } from '../models/Contact';
import { ColDef, SortDirection, ValueGetterParams } from 'ag-grid-community';
import { createHighlightCellRenderer, createNameCellRenderer } from './gridCellRenderers';
import { type FormField } from '../components/common/FormField';
import { filterBySearchTerms } from './searchUtils';

export interface ContactField {
  key: keyof Contact;
  label: string;
  hideEmpty?: boolean;
  fallbackKeys?: Array<keyof Contact>;
}

export const getContactDisplayFields = (): ContactField[] => [
  { key: 'displayName', label: 'Name' },
  { key: 'accountName', label: 'Account', hideEmpty: true },
  { key: 'emailAddress', label: 'Email', hideEmpty: true, fallbackKeys: ['email'] },
  { key: 'officePhoneNumber', label: 'Office Phone', hideEmpty: true, fallbackKeys: ['officePhone', 'phone'] },
  { key: 'cellPhoneNumber', label: 'Cell Phone', hideEmpty: true, fallbackKeys: ['cellPhone'] },
];

export const getContactCardDisplayFields = (): ContactField[] => [
  { key: 'accountName', label: 'Account', hideEmpty: true },
  { key: 'emailAddress', label: 'Email', hideEmpty: true, fallbackKeys: ['email'] },
  { key: 'officePhoneNumber', label: 'Office Phone', hideEmpty: true, fallbackKeys: ['officePhone', 'phone'] },
  { key: 'cellPhoneNumber', label: 'Cell Phone', hideEmpty: true, fallbackKeys: ['cellPhone'] },
];

export const getContactFieldValue = (contact: Contact, field: ContactField): Contact[keyof Contact] | undefined => {
  const keys = [field.key, ...(field.fallbackKeys || [])];

  for (const key of keys) {
    const value = contact[key];

    if (typeof value === 'string') {
      if (value.trim()) {
        return value;
      }
      continue;
    }

    if (value !== undefined && value !== null) {
      return value;
    }
  }

  return undefined;
};

export const getContactColumnDefs = (searchQuery: string, schemaFields?: FormField[]): ColDef<Contact>[] => {
  // Base columns that are always present
  const baseColumns = [
      {
        headerName: '',
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'left' as const,
        cellStyle: { borderRight: '1px solid #ddd' },
        valueGetter: (params: ValueGetterParams<Contact>) => {
          const rowIndex = params.node?.rowIndex;
          if (rowIndex === undefined || rowIndex === null) return 0;

          setTimeout(() => {
            params.api.refreshCells({
              rowNodes: [params.node!],
              columns: ['rowNumber'],
              force: true
            });
          }, 100);

          return rowIndex + 1;
        },
        colId: 'rowNumber',
        sortable: false,
        filter: false
      },
    {
      field: 'displayName',
      headerName: 'Name',
      sortable: true,
      width: 300,
      pinned: true,
      cellRenderer: createNameCellRenderer(searchQuery)
    },
    {
      field: 'noteCount',
      headerName: 'Note Count',
      sortable: true,
      sort: 'desc' as SortDirection
    }
  ];

  if (!schemaFields || !schemaFields.length) return baseColumns;

  // Add schema-based columns
  const additionalColumns = schemaFields.map(field => ({
    field: field.name,
    headerName: field.label,
    sortable: true,
    cellRenderer: createHighlightCellRenderer(searchQuery)
  }));

  return [...baseColumns, ...additionalColumns.filter(col => col.field !== 'displayName' && col.field !== 'noteCount')];
};

export const filterContacts = (
  contacts: Contact[],
  searchQuery: string,
  searchFields?: Array<keyof Contact>
) => {
  return filterBySearchTerms(contacts, searchQuery, searchFields);
};
